/**
 * Market Validation — the founder's number next to two independent ones.
 *
 * DEFINITION COMPATIBILITY COMES FIRST, above every figure on the screen. A
 * Swiss-franc 2026 serviceable market compared against a euro 2024 total market
 * produces a number, and that number means nothing. Putting the compatibility
 * banner after the figures would let a reader anchor on a comparison the
 * methodology already refused to make — and when definitions do not align, the
 * comparison chart is not drawn at all.
 *
 * RANGES STAY RANGES. Every bar below is a BAND from low to high with a tick at
 * the base, never a bar whose length is a single number. Collapsing an uncertain
 * range to a point is the most common way a market slide becomes a lie, and it
 * is the exact thing this analysis exists to catch.
 *
 * FOUNDER ASSUMPTIONS AND RESEARCHED EVIDENCE STAY IN SEPARATE ROWS, all the way
 * down to the narrowing factors — each of which says whether it came from a
 * source or from the founder. Merging them would let a founder's own estimate
 * corroborate the founder's own conclusion.
 *
 * THE SCENARIO PREVIEW IS NOT THE ANSWER. It runs the same three formulas the
 * server runs, locally, so a reader can ask "what if the contract value were
 * lower". It is labelled as a preview everywhere it appears, it writes nothing,
 * and the published conclusion above it never moves — a preview that could
 * silently disagree with the published verdict is worse than no preview.
 */

import React, { useState } from 'react';
import { Text, View } from 'react-native';
import Slider from '@react-native-community/slider';
import { RotateCcw } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SectionDivider } from '@/components/SectionDivider';
import { useAnalysisVisuals, useIsWideLayout, type EvidenceTone } from './analysis-visuals';
import { claimFor } from './claim-join';
import {
  AnalysisHero,
  Footnote,
  InsufficientEvidenceNote,
  SectionCard,
  SectionHeading,
} from './AnalysisPrimitives';
import {
  calculateBottomUp,
  describeRangeProblem,
  formatCompactAmount,
  formatCompactRange,
  type Range,
} from '@/lib/strategic-analysis/market-calculations';
import type {
  ClaimView,
  MarketAssumptions,
  MarketConclusionName,
  MarketValidationBody,
} from '@/lib/strategic-analysis/types';

const CONCLUSION_LABELS: Record<MarketConclusionName, string> = {
  supported: 'Supported',
  plausible_with_caveats: 'Plausible, with caveats',
  potentially_overstated: 'Potentially overstated',
  potentially_understated: 'Potentially understated',
  not_comparable: 'Not comparable',
  insufficient_evidence: 'Insufficient evidence',
};

const CONCLUSION_TONE: Record<MarketConclusionName, EvidenceTone> = {
  supported: 'positive',
  plausible_with_caveats: 'caution',
  potentially_overstated: 'adverse',
  potentially_understated: 'adverse',
  not_comparable: 'unknown',
  insufficient_evidence: 'unknown',
};

/** One band on the comparison chart. */
interface Series {
  label: string;
  range: Range;
  /** Founder-supplied bands are drawn and labelled differently from researched ones. */
  origin: 'founder' | 'independent' | 'corroborated';
  note?: string;
}

interface MarketValidationProps {
  body: MarketValidationBody;
  /** The BOUND assumption version, not today's latest. */
  assumptions: MarketAssumptions | null;
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
  onEditAssumptions?: () => void;
}

export function MarketValidation({
  body,
  assumptions,
  claims,
  onInspectClaim,
  onEditAssumptions,
}: MarketValidationProps) {
  const v = useAnalysisVisuals();
  const wide = useIsWideLayout();
  const currency = assumptions?.currency ?? 'CHF';
  const tone = v.tone(CONCLUSION_TONE[body.conclusion]);

  // Exactly the four things worth putting side by side, and only where the
  // server published them. `founderSam` is the level the methodology itself
  // compares against — a founder TAM plotted next to two SAMs would be a
  // different quantity wearing the same axis.
  const series: Series[] = [];
  if (assumptions?.founderSam) {
    series.push({
      label: 'Founder estimate',
      range: assumptions.founderSam,
      origin: 'founder',
      note: 'Unverified founder input',
    });
  }
  if (body.topDown.supported && body.topDown.range) {
    series.push({ label: 'Independent — top-down', range: body.topDown.range, origin: 'independent' });
  }
  if (body.bottomUp.supported && body.bottomUp.sam) {
    series.push({ label: 'Independent — bottom-up', range: body.bottomUp.sam, origin: 'independent' });
  }
  if (body.corroboratedRange) {
    series.push({
      label: body.methodConflict ? 'Combined envelope' : 'Corroborated range',
      range: body.corroboratedRange,
      origin: 'corroborated',
      note: body.methodConflict ? 'The two methods did not overlap' : undefined,
    });
  }

  const columns = wide ? 'flex-row items-start gap-4' : 'gap-4';

  return (
    <View className="gap-4">
      {/* Compatibility first. Always. */}
      <View
        accessibilityRole="summary"
        accessibilityLabel={
          body.definitionCompatible
            ? 'Market definitions align, so the figures are comparable.'
            : 'Market definitions do not align, so no comparison was made.'
        }
        className="rounded-2xl p-4 gap-2"
        style={{
          backgroundColor: body.definitionCompatible ? v.colors.traffic.greenLight : v.colors.amber.light,
          overflow: 'hidden',
        }}
      >
        <Text
          className="text-[13px] font-semibold"
          style={{ color: body.definitionCompatible ? v.colors.success : v.colors.amber.main }}
        >
          {body.definitionCompatible
            ? 'These figures are measuring the same thing'
            : 'These figures are not measuring the same thing'}
        </Text>
        {body.definitionNotes.map((note, i) => (
          <Text key={`note-${i}`} className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
            {note}
          </Text>
        ))}
        {!body.definitionCompatible && (
          <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
            No comparison was made. A figure computed across different currencies, base years,
            customer units or market boundaries would not mean anything.
          </Text>
        )}
      </View>

      <AnalysisHero
        label="Validation result"
        headline={CONCLUSION_LABELS[body.conclusion]}
        body={body.conclusionExplanation}
        accessibilityLabel={`Validation result: ${CONCLUSION_LABELS[body.conclusion]}. ${body.conclusionExplanation}`}
      />

      <View className={columns}>
        <View className="gap-4" style={wide ? { flex: 3, minWidth: 0 } : undefined}>
          {/* The comparison. Not drawn at all when the definitions do not align —
              a chart is a stronger claim of comparability than a sentence. */}
          {body.definitionCompatible && series.length > 0 && (
            <View
              className="rounded-3xl p-4 gap-3"
              style={{
                backgroundColor: v.chart.surface,
                borderWidth: 1,
                borderColor: v.chart.border,
                overflow: 'hidden',
              }}
            >
              <SectionHeading title="Compare the estimates" note={`${currency} · base year ${assumptions?.baseYear ?? ''}`} />
              <ComparisonChart series={series} currency={currency} tone={tone.fore} />
              {assumptions?.marketDefinition && (
                <View className="rounded-2xl p-3" style={{ backgroundColor: v.colors.surface.secondary }}>
                  <Text className="text-[12px] leading-4" style={{ color: v.colors.text.secondary }}>
                    <Text className="font-semibold">Market definition: </Text>
                    {assumptions.marketDefinition}
                  </Text>
                </View>
              )}
            </View>
          )}

          {!body.definitionCompatible && (
            <InsufficientEvidenceNote
              title="No comparison chart"
              detail="The founder assumptions and the researched evidence do not describe the same market, so plotting them on one axis would assert a comparison the methodology refused to make."
            />
          )}

          {body.definitionCompatible && series.length === 0 && (
            <InsufficientEvidenceNote
              title="Nothing to compare yet"
              detail="Neither method produced a publishable range, and no founder serviceable-market figure was bound to this analysis."
            />
          )}

          <ScenarioPreview assumptions={assumptions} currency={currency} />
        </View>

        <View className="gap-4" style={wide ? { flex: 2, minWidth: 0 } : undefined}>
          <WhyTheyDiffer body={body} currency={currency} />

          <MethodDetail
            body={body}
            assumptions={assumptions}
            currency={currency}
            claims={claims}
            onInspectClaim={onInspectClaim}
            onEditAssumptions={onEditAssumptions}
          />

          {/* THE COMPARISON'S OWN SOURCES. This was a flat list of every claim
              in the run, rendered under a heading and left for the reader to
              guess which statement belonged to which number — the one body with
              no join at all. Each figure now carries its own link, and this is
              only the conclusion's. */}
          <ClaimSources
            claim={claimFor(claims, body.comparisonClaimKey)}
            label="the corroborated range"
            onInspectClaim={onInspectClaim}
          />
        </View>
      </View>

      <Footnote>
        These are decision-support ranges, not audited forecasts. Every result depends on the market
        definition and assumptions shown above.
      </Footnote>
    </View>
  );
}

// ---------------------------------------------------------------------------
// The comparison chart
// ---------------------------------------------------------------------------

/**
 * Bands, not bars.
 *
 * Each series is drawn from its low bound to its high bound with a tick at the
 * base, on one shared scale. A reader can see overlap — which is the entire
 * question the methodology asks — without reading a single number.
 */
function ComparisonChart({
  series,
  currency,
  tone,
}: {
  series: Series[];
  currency: string;
  tone: string;
}) {
  const v = useAnalysisVisuals();
  const max = Math.max(...series.map((s) => s.range.high), 0) || 1;

  const colourFor = (origin: Series['origin']) =>
    origin === 'founder'
      ? v.colors.amber.main
      : origin === 'corroborated'
      ? tone
      : v.colors.primary[600];

  return (
    <View className="gap-3.5">
      {series.map((entry, index) => {
        const lowPct = Math.max(0, Math.min(1, entry.range.low / max));
        const highPct = Math.max(0, Math.min(1, entry.range.high / max));
        const basePct = Math.max(0, Math.min(1, entry.range.base / max));
        const colour = colourFor(entry.origin);

        return (
          <View
            key={`series-${index}`}
            accessibilityRole="summary"
            accessibilityLabel={`${entry.label}: ${formatCompactRange(entry.range, currency)}${entry.note ? `. ${entry.note}` : ''}`}
            className="gap-1.5"
          >
            <View className="flex-row items-end gap-2">
              <Text numberOfLines={2} className="flex-1 text-[12px] leading-4" style={{ color: v.colors.text.secondary }}>
                {entry.label}
              </Text>
              <Text numberOfLines={1} className="text-[12px] font-semibold shrink-0" style={{ color: v.colors.text.primary }}>
                {formatCompactRange(entry.range, currency)}
              </Text>
            </View>

            <View
              className="rounded-full overflow-hidden"
              style={{ height: 12, backgroundColor: v.colors.surface.secondary }}
            >
              <View
                style={{
                  position: 'absolute',
                  left: `${lowPct * 100}%`,
                  // A minimum width so a very narrow band is still visible as a
                  // band rather than disappearing into a hairline.
                  width: `${Math.max(highPct - lowPct, 0.015) * 100}%`,
                  top: 0,
                  bottom: 0,
                  borderRadius: 999,
                  backgroundColor: colour,
                  opacity: entry.origin === 'founder' ? 0.55 : 1,
                }}
              />
              <View
                style={{
                  position: 'absolute',
                  left: `${basePct * 100}%`,
                  top: 1,
                  bottom: 1,
                  width: 2,
                  backgroundColor: v.colors.surface.primary,
                }}
              />
            </View>

            {entry.note ? (
              <Text numberOfLines={2} className="text-[10px]" style={{ color: v.colors.text.tertiary }}>
                {entry.note}
              </Text>
            ) : null}
          </View>
        );
      })}

      <Text className="text-[10px] leading-4" style={{ color: v.colors.text.tertiary }}>
        Each band runs from the low bound to the high bound; the tick marks the base case. Bands are
        never collapsed to a single figure.
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Scenario preview
// ---------------------------------------------------------------------------

/**
 * The what-if, with no authority whatsoever.
 *
 * It runs `calculateBottomUp` — the exact mirror of the server's arithmetic,
 * held in parity by `market-parity-fixtures` — over base-case inputs the reader
 * can move. It never writes, never re-renders the conclusion above it, and says
 * so on screen. `Reset` returns to the assumptions the published report used.
 */
function ScenarioPreview({
  assumptions,
  currency,
}: {
  assumptions: MarketAssumptions | null;
  currency: string;
}) {
  const v = useAnalysisVisuals();
  const [customers, setCustomers] = useState<number | null>(null);
  const [value, setValue] = useState<number | null>(null);
  const [share, setShare] = useState<number | null>(null);

  if (!assumptions) {
    return (
      <InsufficientEvidenceNote
        title="No scenario preview"
        detail="No founder assumption version was bound to this analysis, so there are no base-case inputs to vary."
      />
    );
  }

  const baseCustomers = assumptions.serviceableCustomerUnits.base;
  const baseValue = assumptions.annualValuePerCustomer.base;
  const baseShare = assumptions.reachableShare.base;

  const currentCustomers = customers ?? baseCustomers;
  const currentValue = value ?? baseValue;
  const currentShare = share ?? baseShare;
  const touched = customers !== null || value !== null || share !== null;

  const point = (n: number): Range => ({ low: n, base: n, high: n });
  const problem =
    describeRangeProblem('serviceableCustomerUnits', point(currentCustomers)) ??
    describeRangeProblem('annualValuePerCustomer', point(currentValue)) ??
    describeRangeProblem('reachableShare', point(currentShare));

  let result: { sam: Range; som: Range } | null = null;
  let failure: string | null = problem;
  if (!problem) {
    try {
      const computed = calculateBottomUp({
        serviceableCustomerUnits: point(currentCustomers),
        annualValuePerCustomer: point(currentValue),
        reachableShare: point(currentShare),
      });
      result = { sam: computed.sam, som: computed.som };
    } catch (error) {
      failure = error instanceof Error ? error.message : 'This combination cannot be computed.';
    }
  }

  return (
    <View
      className="rounded-3xl p-4 gap-3"
      style={{
        backgroundColor: v.chart.surface,
        borderWidth: 1,
        borderColor: v.chart.border,
        overflow: 'hidden',
      }}
    >
      <View className="flex-row items-start gap-2">
        <View className="flex-1">
          <SectionHeading title="Test the bottom-up assumptions" />
          <Text className="mt-0.5 text-[11px]" style={{ color: v.colors.text.tertiary }}>
            A working preview. It changes nothing and is never the published figure.
          </Text>
        </View>
        {touched && (
          <View className="rounded-xl overflow-hidden shrink-0" style={{ height: 34, backgroundColor: v.colors.surface.secondary }}>
            <AnimatedPressable
              onPress={() => {
                setCustomers(null);
                setValue(null);
                setShare(null);
              }}
              accessibilityRole="button"
              accessibilityLabel="Reset scenario to the published assumptions"
              className="flex-row items-center gap-1.5 px-3"
              hitSlop={{ top: 6, bottom: 6 }}
              style={{ flex: 1, minHeight: 34 }}
            >
              <RotateCcw size={13} color={v.colors.primary[700]} />
              <Text className="text-[12px] font-semibold" style={{ color: v.colors.primary[700] }}>
                Reset
              </Text>
            </AnimatedPressable>
          </View>
        )}
      </View>

      <ScenarioSlider
        label={`Serviceable ${assumptions.customerUnit}s`}
        value={currentCustomers}
        display={Math.round(currentCustomers).toLocaleString('en-CH')}
        minimum={Math.max(1, Math.round(baseCustomers * 0.25))}
        maximum={Math.max(2, Math.round(baseCustomers * 2))}
        step={Math.max(1, Math.round(baseCustomers / 100))}
        onChange={setCustomers}
      />
      <ScenarioSlider
        label="Annual value per customer"
        value={currentValue}
        display={formatCompactAmount(currentValue, currency)}
        minimum={Math.max(1, Math.round(baseValue * 0.25))}
        maximum={Math.max(2, Math.round(baseValue * 2))}
        step={Math.max(1, Math.round(baseValue / 100))}
        onChange={setValue}
      />
      <ScenarioSlider
        label="Reachable share in five years"
        value={currentShare}
        display={`${(currentShare * 100).toFixed(1)}%`}
        minimum={0.001}
        maximum={1}
        step={0.001}
        onChange={setShare}
      />

      {failure ? (
        <Text className="text-[12px] leading-4" style={{ color: v.colors.amber.main }}>
          {failure}
        </Text>
      ) : result ? (
        <View className="gap-2">
          <FormulaRow
            formula={`${Math.round(currentCustomers).toLocaleString('en-CH')} × ${formatCompactAmount(currentValue, currency)}`}
            result={`${formatCompactAmount(result.sam.base, currency)} SAM`}
          />
          <FormulaRow
            formula={`SAM × ${(currentShare * 100).toFixed(1)}%`}
            result={`${formatCompactAmount(result.som.base, currency)} SOM`}
          />
        </View>
      ) : null}

      <Text className="text-[10px] leading-4" style={{ color: v.colors.text.tertiary }}>
        Preview only — the published conclusion above is unchanged, and nothing here is saved.
        Reachable share is an estimate, never an observed fact.
      </Text>
    </View>
  );
}

function ScenarioSlider({
  label,
  value,
  display,
  minimum,
  maximum,
  step,
  onChange,
}: {
  label: string;
  value: number;
  display: string;
  minimum: number;
  maximum: number;
  step: number;
  onChange: (next: number) => void;
}) {
  const v = useAnalysisVisuals();
  return (
    <View className="gap-1">
      <View className="flex-row items-center gap-2">
        <Text numberOfLines={1} className="flex-1 text-[12px]" style={{ color: v.colors.text.secondary }}>
          {label}
        </Text>
        <Text numberOfLines={1} className="text-[12px] font-semibold shrink-0" style={{ color: v.colors.text.primary }}>
          {display}
        </Text>
      </View>
      <Slider
        accessibilityLabel={label}
        value={value}
        minimumValue={minimum}
        maximumValue={maximum}
        step={step}
        onValueChange={onChange}
        minimumTrackTintColor={v.colors.primary[600]}
        maximumTrackTintColor={v.colors.surface.tertiary}
        thumbTintColor={v.colors.primary[600]}
        style={{ height: 34 }}
      />
    </View>
  );
}

function FormulaRow({ formula, result }: { formula: string; result: string }) {
  const v = useAnalysisVisuals();
  return (
    <View
      className="flex-row items-center gap-2 rounded-2xl px-3 py-2.5"
      style={{ backgroundColor: v.colors.surface.secondary }}
    >
      <Text numberOfLines={2} className="flex-1 text-[12px]" style={{ color: v.colors.text.secondary }}>
        {formula}
      </Text>
      <Text numberOfLines={1} className="text-[13px] font-semibold shrink-0" style={{ color: v.colors.text.primary }}>
        {result}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Why the estimates differ, and how each was built
// ---------------------------------------------------------------------------

function WhyTheyDiffer({ body, currency }: { body: MarketValidationBody; currency: string }) {
  const v = useAnalysisVisuals();
  const reasons: string[] = [];

  if (!body.definitionCompatible) {
    reasons.push(...body.definitionNotes);
  }
  if (body.methodConflict) {
    reasons.push(
      'The two methods did not overlap. Their combined envelope is shown with a caveat rather than as a corroborated figure.',
    );
  }
  const founderFactors = body.topDown.narrowingFactors.filter((f) => f.founderProvided);
  if (founderFactors.length > 0) {
    reasons.push(
      `${founderFactors.length} narrowing factor(s) came from the founder rather than from a source, so the top-down result inherits an unverified input.`,
    );
  }
  if (!body.topDown.supported) {
    reasons.push('No evidenced published base market was accepted, so top-down was not computed.');
  }
  if (!body.bottomUp.supported) {
    reasons.push('Bottom-up was not computed: the customer and value assumptions were incomplete.');
  }
  if (!body.corroboratedRange) {
    reasons.push(
      'No reference range is published. Either only one method had enough evidence, or the two differed by more than 2×, and averaging methods that disagree that much would manufacture a figure neither of them supports.',
    );
  }

  return (
    <View className="gap-2">
      <SectionDivider label="Why the estimates differ" marginTop={4} marginBottom={4} />
      <SectionCard>
      {reasons.length === 0 ? (
        <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
          The two independent methods overlapped and every narrowing factor came from a source, so
          the ranges differ only by the width of their own bounds.
        </Text>
      ) : (
        reasons.map((reason, i) => (
          <View key={`reason-${i}`} className="flex-row items-start gap-2">
            <View
              className="rounded-full shrink-0"
              style={{ width: 5, height: 5, marginTop: 7, backgroundColor: v.colors.amber.main }}
            />
            <Text className="flex-1 text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
              {reason}
            </Text>
          </View>
        ))
      )}
      {body.corroboratedRange && (
        <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
          {body.methodConflict ? 'Combined envelope' : 'Corroborated range'}:{' '}
          {formatCompactRange(body.corroboratedRange, currency)}
        </Text>
      )}
      </SectionCard>
    </View>
  );
}

function MethodDetail({
  body,
  assumptions,
  currency,
  claims,
  onInspectClaim,
  onEditAssumptions,
}: {
  body: MarketValidationBody;
  assumptions: MarketAssumptions | null;
  currency: string;
  /** Passed down so every FIGURE carries its own sources, not the card. */
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
  onEditAssumptions?: () => void;
}) {
  const v = useAnalysisVisuals();

  return (
    <View className="gap-3">
      <SectionDivider label="How each figure was built" marginTop={4} marginBottom={4} />

      {/* Founder assumptions — their own block, labelled as theirs. */}
      <Block title="Your assumptions" tone="founder">
        {!assumptions ? (
          <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
            No founder assumptions were bound to this analysis.
          </Text>
        ) : (
          <>
            <Row label="Customer unit" value={assumptions.customerUnit} />
            <Row label="Base year" value={String(assumptions.baseYear)} />
            <Row label="Currency" value={assumptions.currency} />
            {assumptions.founderTam && <Row label="Your TAM" value={formatCompactRange(assumptions.founderTam, currency)} />}
            {assumptions.founderSam && <Row label="Your SAM" value={formatCompactRange(assumptions.founderSam, currency)} />}
            {assumptions.founderSom && <Row label="Your SOM" value={formatCompactRange(assumptions.founderSom, currency)} />}
            <Text className="text-[12px] leading-4" style={{ color: v.colors.text.secondary }}>
              Founder-provided. These are not independently verified, and they are never used to
              support the independent estimate.
            </Text>
            {onEditAssumptions && (
              <Text
                accessibilityRole="button"
                onPress={onEditAssumptions}
                className="text-[13px] font-semibold"
                style={{ color: v.colors.primary[700] }}
              >
                Edit assumptions
              </Text>
            )}
          </>
        )}
      </Block>

      {/* Top-down: every narrowing step, with where it came from. */}
      <Block title="Independent — top-down" tone="independent">
        {!body.topDown.supported ? (
          <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
            No evidenced published base market was accepted, so top-down was not computed.
          </Text>
        ) : (
          <>
            {body.topDown.base && (
              <View className="gap-1">
                <Row
                  label="Published base market"
                  value={formatCompactAmount(body.topDown.base.value, body.topDown.base.currency ?? currency)}
                />
                <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
                  {body.topDown.base.geography} · base year {body.topDown.base.baseYear} ·{' '}
                  {body.topDown.base.method}
                </Text>
                <ClaimSources
                  claim={claimFor(claims, body.topDown.claimKey)}
                  label="the published base market"
                  onInspectClaim={onInspectClaim}
                />
              </View>
            )}
            {body.bottomUp.inputs.map((input, i) => (
              <View key={`bu-input-${i}`} className="gap-1">
                <View className="flex-row items-start gap-2">
                  <Text numberOfLines={2} className="flex-1 text-[13px]" style={{ color: v.colors.text.secondary }}>
                    {input.label}
                  </Text>
                  <Text numberOfLines={1} className="shrink-0 text-[13px]" style={{ color: v.colors.text.primary }}>
                    {input.low} – {input.high}
                  </Text>
                </View>
                <ClaimSources
                  claim={claimFor(claims, input.claimKey)}
                  label={input.label}
                  onInspectClaim={onInspectClaim}
                />
              </View>
            ))}
            {body.topDown.narrowingFactors.map((factor, i) => (
              <View key={`factor-${i}`} className="gap-1">
              <View className="flex-row items-start gap-2">
                <Text numberOfLines={2} className="flex-1 text-[13px]" style={{ color: v.colors.text.secondary }}>
                  × {factor.label}
                </Text>
                <View className="shrink-0 items-end" style={{ maxWidth: '50%' }}>
                  <Text numberOfLines={1} className="text-[13px]" style={{ color: v.colors.text.primary }}>
                    {factor.low} – {factor.high}
                  </Text>
                  <Text
                    numberOfLines={1}
                    className="text-[11px]"
                    style={{ color: factor.founderProvided ? v.colors.amber.main : v.colors.text.tertiary }}
                  >
                    {factor.founderProvided ? 'Founder-provided' : 'From evidence'}
                  </Text>
                </View>
              </View>
              <ClaimSources
                claim={claimFor(claims, factor.claimKey)}
                label={factor.label}
                onInspectClaim={onInspectClaim}
              />
              </View>
            ))}
            {body.topDown.range && <Row label="Top-down result" value={formatCompactRange(body.topDown.range, currency)} />}
          </>
        )}
      </Block>

      {/* Bottom-up: the fixed formulas, shown as formulas. */}
      <Block title="Independent — bottom-up" tone="independent">
        {!body.bottomUp.supported ? (
          <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
            Bottom-up was not computed: the customer and value assumptions were incomplete.
          </Text>
        ) : (
          <>
            {body.bottomUp.tam ? (
              <Row label="TAM" value={formatCompactRange(body.bottomUp.tam, currency)} />
            ) : (
              <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
                TAM not computed: total potential customer units were not provided. Reusing the
                serviceable count would assert you can serve the entire market.
              </Text>
            )}
            {body.bottomUp.sam && <Row label="SAM" value={formatCompactRange(body.bottomUp.sam, currency)} />}
            {body.bottomUp.som && <Row label="SOM" value={formatCompactRange(body.bottomUp.som, currency)} />}
            <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
              SAM = serviceable customer units × annual value per customer. SOM = SAM × realistically
              reachable share. Low and high bounds combine bound-for-bound, which is the widest
              honest envelope.
            </Text>
            <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
              Reachable share is an estimate, never an observed fact.
            </Text>
          </>
        )}
      </Block>
    </View>
  );
}

/**
 * One method's working, tinted by WHERE ITS NUMBERS CAME FROM.
 *
 * The colour carries the same distinction the copy does — founder-supplied
 * versus independently researched — which is the single most important reading
 * on this screen. It is deliberately NOT decorative, and deliberately not
 * green: in this product green means "supported by evidence", so tinting an
 * explanation green would assert a verdict the block does not carry.
 */
function Block({
  title,
  tone = 'neutral',
  children,
}: {
  title: string;
  tone?: 'founder' | 'independent' | 'neutral';
  children: React.ReactNode;
}) {
  const v = useAnalysisVisuals();
  const background = tone === 'founder'
    ? v.colors.amber.light
    : tone === 'independent'
    ? v.colors.primary[50]
    : v.colors.surface.primary;
  // `primary[800]` is deep teal in light and pale mint in dark, and each sits
  // correctly on its own theme's `primary[50]` — so the index does not flip.
  const label = tone === 'founder'
    ? v.colors.amber.main
    : tone === 'independent'
    ? v.colors.primary[800]
    : v.colors.text.primary;

  return (
    <View
      className="rounded-2xl p-4 gap-2"
      style={{
        backgroundColor: background,
        borderWidth: 1,
        borderColor: tone === 'neutral' ? v.colors.border.light : 'transparent',
        overflow: 'hidden',
      }}
    >
      <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: label }}>
        {title}
      </Text>
      {children}
    </View>
  );
}


function Row({ label, value }: { label: string; value: string }) {
  const v = useAnalysisVisuals();
  return (
    <View className="flex-row items-start gap-2">
      <Text numberOfLines={2} className="flex-1 text-[13px]" style={{ color: v.colors.text.secondary }}>
        {label}
      </Text>
      <Text
        numberOfLines={2}
        className="text-[13px] font-semibold text-right shrink-0"
        style={{ maxWidth: '58%', color: v.colors.text.primary }}
      >
        {value}
      </Text>
    </View>
  );
}

/**
 * One figure's sources, or nothing.
 *
 * NOTHING is the honest outcome when no claim resolves: the claim was rejected
 * at validation and there is no source to open. A control that opens an empty
 * modal is worse than no control, because it implies there was something to see.
 */
function ClaimSources({
  claim,
  label,
  onInspectClaim,
}: {
  claim: ClaimView | null;
  label: string;
  onInspectClaim?: (claim: ClaimView) => void;
}) {
  const v = useAnalysisVisuals();
  if (!claim || !onInspectClaim || claim.evidenceIds.length === 0) return null;
  return (
    <Text
      accessibilityRole="button"
      accessibilityLabel={`View sources for ${label}`}
      onPress={() => onInspectClaim(claim)}
      className="text-[12px] font-semibold"
      style={{ color: v.colors.primary[700] }}
    >
      View sources
    </Text>
  );
}
